import { createBrowserRouter } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import RootLayout from './layouts/RootLayout'
import AuthLayout from './layouts/AuthLayout'
import DashboardLayout from './layouts/DashboardLayout'
import ProtectedRoute from './ProtectedRoute'
import ErrorPage from '@/pages/ErrorPage'

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
      <p className="text-sm font-medium text-slate-700">Loading...</p>
    </div>
  </div>
)

// Lazy page wrapper
const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
)

// Lazy load all pages
const Home = lazy(() => import('@/pages/Home'))
const Contact = lazy(() => import('@/pages/Contact'))

// Auth pages
const SignIn = lazy(() => import('@/pages/auth/SignIn'))
const SignUp = lazy(() => import('@/pages/auth/SignUp'))
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'))

// Dashboard pages
const Dashboard = lazy(() => import('@/pages/dashboard/Dashboard'))

// Projects
const Projects = lazy(() => import('@/pages/dashboard/projects/Projects'))
const NewProject = lazy(() => import('@/pages/dashboard/projects/NewProject'))
const ProjectDetails = lazy(() => import('@/pages/dashboard/projects/ProjectDetails'))
const EditProject = lazy(() => import('@/pages/dashboard/projects/EditProject'))
const NewPurchaseEntry = lazy(() => import('@/pages/dashboard/projects/purchase-entry/NewPurchaseEntry'))
const EditPurchaseEntry = lazy(() => import('@/pages/dashboard/projects/purchase-entry/EditPurchaseEntry'))
const NewProductionEntry = lazy(() => import('@/pages/dashboard/projects/production-entry/NewProductionEntry'))
const EditProductionEntry = lazy(() => import('@/pages/dashboard/projects/production-entry/EditProductionEntry'))

// Farmers
const Farmers = lazy(() => import('@/pages/dashboard/farmers/Farmers'))
const NewFarmer = lazy(() => import('@/pages/dashboard/farmers/NewFarmer'))
const FarmerDetails = lazy(() => import('@/pages/dashboard/farmers/FarmerDetails'))

// Buyers
const Buyers = lazy(() => import('@/pages/dashboard/buyers/Buyers'))
const NewBuyer = lazy(() => import('@/pages/dashboard/buyers/NewBuyer'))
const BuyerDetails = lazy(() => import('@/pages/dashboard/buyers/BuyerDetails'))

// Workers
const Workers = lazy(() => import('@/pages/dashboard/workers/Workers'))
const NewWorker = lazy(() => import('@/pages/dashboard/workers/NewWorker'))
const WorkerDetails = lazy(() => import('@/pages/dashboard/workers/WorkerDetails'))

// Expenses
const Expenses = lazy(() => import('@/pages/dashboard/expenses/Expenses'))
const NewExpense = lazy(() => import('@/pages/dashboard/expenses/NewExpense'))

// Income
const Income = lazy(() => import('@/pages/dashboard/income/Income'))
const NewIncome = lazy(() => import('@/pages/dashboard/income/NewIncome'))

// Sales
const Sales = lazy(() => import('@/pages/dashboard/sales/Sales'))
const NewSale = lazy(() => import('@/pages/dashboard/sales/NewSale'))
const SaleDetails = lazy(() => import('@/pages/dashboard/sales/SaleDetails'))

// Admin
const ContactSubmissions = lazy(() => import('@/pages/admin/ContactSubmissions'))

// Reports, Settings & Profile
const Reports = lazy(() => import('@/pages/dashboard/reports/Reports'))
const FarmerLedger = lazy(() => import('@/pages/dashboard/reports/FarmerLedger'))
const ProjectCostReport = lazy(() => import('@/pages/dashboard/reports/ProjectCostReport'))
const BuyerSalesReport = lazy(() => import('@/pages/dashboard/reports/BuyerSalesReport'))
const Settings = lazy(() => import('@/pages/dashboard/settings/Settings'))
const Profile = lazy(() => import('@/pages/dashboard/profile/Profile'))

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: <LazyPage><Home /></LazyPage>,
      },
      {
        path: 'contact',
        element: <LazyPage><Contact /></LazyPage>,
      },
      {
        element: <AuthLayout />,
        children: [
          {
            path: 'signin',
            element: <LazyPage><SignIn /></LazyPage>,
          },
          {
            path: 'signup',
            element: <LazyPage><SignUp /></LazyPage>,
          },
          {
            path: 'forgot-password',
            element: <LazyPage><ForgotPassword /></LazyPage>,
          },
        ],
      },
      {
        element: <ProtectedRoute><DashboardLayout /></ProtectedRoute>,
        errorElement: <ErrorPage />,
        children: [
          {
            path: 'dashboard',
            element: <LazyPage><Dashboard /></LazyPage>,
          },
          {
            path: 'projects',
            children: [
              {
                index: true,
                element: <LazyPage><Projects /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewProject /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><ProjectDetails /></LazyPage>,
              },
              {
                path: ':id/edit',
                element: <LazyPage><EditProject /></LazyPage>,
              },
              {
                path: ':id/purchase-entry/new',
                element: <LazyPage><NewPurchaseEntry /></LazyPage>,
              },
              {
                path: ':id/purchase-entry/:entryId',
                element: <LazyPage><EditPurchaseEntry /></LazyPage>,
              },
              {
                path: ':id/production-entry/new',
                element: <LazyPage><NewProductionEntry /></LazyPage>,
              },
              {
                path: ':id/production-entry/:entryId',
                element: <LazyPage><EditProductionEntry /></LazyPage>,
              },
            ],
          },
          {
            path: 'farmers',
            children: [
              {
                index: true,
                element: <LazyPage><Farmers /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewFarmer /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><FarmerDetails /></LazyPage>,
              },
            ],
          },
          {
            path: 'buyers',
            children: [
              {
                index: true,
                element: <LazyPage><Buyers /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewBuyer /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><BuyerDetails /></LazyPage>,
              },
            ],
          },
          {
            path: 'workers',
            children: [
              {
                index: true,
                element: <LazyPage><Workers /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewWorker /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><WorkerDetails /></LazyPage>,
              },
            ],
          },
          {
            path: 'expenses',
            children: [
              {
                index: true,
                element: <LazyPage><Expenses /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewExpense /></LazyPage>,
              },
            ],
          },
          {
            path: 'income',
            children: [
              {
                index: true,
                element: <LazyPage><Income /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewIncome /></LazyPage>,
              },
            ],
          },
          {
            path: 'sales',
            children: [
              {
                index: true,
                element: <LazyPage><Sales /></LazyPage>,
              },
              {
                path: 'new',
                element: <LazyPage><NewSale /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><SaleDetails /></LazyPage>,
              },
            ],
          },
          {
            path: 'reports',
            children: [
              { index: true, element: <LazyPage><Reports /></LazyPage> },
              { path: 'farmer-ledger', element: <LazyPage><FarmerLedger /></LazyPage> },
              { path: 'project-cost', element: <LazyPage><ProjectCostReport /></LazyPage> },
              { path: 'buyer-sales', element: <LazyPage><BuyerSalesReport /></LazyPage> },
            ],
          },
          {
            path: 'settings',
            element: <LazyPage><Settings /></LazyPage>,
          },
          {
            path: 'profile',
            element: <LazyPage><Profile /></LazyPage>,
          },
          {
            path: 'dashboard/admin/contacts',
            element: <LazyPage><ContactSubmissions /></LazyPage>,
          },
        ],
      },
    ],
  },
])
